import { useEffect, useMemo, useRef } from "react";

interface CommentLocation {
  type: "comment" | "reply";
  parentId: number | null;
  commentId: number;
}

interface CommentLike {
  id: number | string;
  comments?: CommentLike[];
}

interface UseCommentHighlightingProps {
  highlightCommentIds: number[];
  highlightReplyParentById: ReadonlyMap<number, number>;
  visibleCommentIds: ReadonlySet<number>;
  allComments: CommentLike[];
  isExpanded: boolean;
  hasMoreComments: boolean;
  isLoadingMoreComments: boolean;
  handleLoadMoreComments: () => Promise<void>;
  expandAllComments: () => void;
  expandRepliesForComment: (commentId: number) => void;
  shouldScrollToComment: boolean;
  onHighlightLocated?: (commentId: number) => void;
  onHighlightExhausted?: () => void;
}

export const useCommentHighlighting = ({
  highlightCommentIds,
  highlightReplyParentById,
  visibleCommentIds,
  allComments,
  isExpanded,
  hasMoreComments,
  isLoadingMoreComments,
  handleLoadMoreComments,
  expandAllComments,
  expandRepliesForComment,
  shouldScrollToComment,
  onHighlightLocated,
  onHighlightExhausted,
}: UseCommentHighlightingProps) => {
  const commentLocationById = useMemo(() => {
    const locations = new Map<number, CommentLocation>();

    const registerReplies = (
      replies: CommentLike[],
      topLevelCommentId: number,
    ) => {
      for (const reply of replies) {
        const replyId = Number(reply.id);
        if (Number.isNaN(replyId)) continue;

        locations.set(replyId, {
          type: "reply",
          parentId: topLevelCommentId,
          commentId: replyId,
        });
        registerReplies(reply.comments ?? [], topLevelCommentId);
      }
    };

    for (const comment of allComments) {
      const commentId = Number(comment.id);
      if (Number.isNaN(commentId)) continue;

      locations.set(commentId, {
        type: "comment",
        parentId: null,
        commentId,
      });

      registerReplies(comment.comments ?? [], commentId);
    }

    return locations;
  }, [allComments]);

  const highlightLocation = useMemo(() => {
    if (highlightCommentIds.length === 0) return null;

    for (const targetId of highlightCommentIds) {
      const location = commentLocationById.get(targetId);
      if (location && !visibleCommentIds.has(targetId)) return location;
      const parentId = highlightReplyParentById.get(targetId);
      const parentLocation = parentId
        ? commentLocationById.get(parentId)
        : undefined;
      const topLevelParentId =
        parentLocation?.type === "reply"
          ? parentLocation.parentId
          : parentLocation?.type === "comment"
            ? parentLocation.commentId
            : null;
      if (topLevelParentId && !visibleCommentIds.has(targetId)) {
        return {
          type: "reply",
          parentId: topLevelParentId,
          commentId: targetId,
        };
      }
    }

    for (const targetId of highlightCommentIds) {
      const location = commentLocationById.get(targetId);
      if (location) return location;
      const parentId = highlightReplyParentById.get(targetId);
      const parentLocation = parentId
        ? commentLocationById.get(parentId)
        : undefined;
      const topLevelParentId =
        parentLocation?.type === "reply"
          ? parentLocation.parentId
          : parentLocation?.type === "comment"
            ? parentLocation.commentId
            : null;
      if (topLevelParentId) {
        return {
          type: "reply",
          parentId: topLevelParentId,
          commentId: targetId,
        };
      }
    }

    return null;
  }, [
    highlightCommentIds,
    highlightReplyParentById,
    commentLocationById,
    visibleCommentIds,
  ]);

  const expansionRequestedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!highlightLocation) return;
    if (visibleCommentIds.has(highlightLocation.commentId)) return;

    const expansionKey = `${highlightLocation.type}:${highlightLocation.parentId ?? "root"}:${highlightLocation.commentId}`;
    if (expansionRequestedKeyRef.current === expansionKey) return;
    expansionRequestedKeyRef.current = expansionKey;

    if (!isExpanded) {
      expandAllComments();
    }

    if (highlightLocation.type === "reply" && highlightLocation.parentId) {
      expandRepliesForComment(highlightLocation.parentId);
    }
  }, [
    highlightLocation,
    visibleCommentIds,
    isExpanded,
    expandAllComments,
    expandRepliesForComment,
  ]);

  useEffect(() => {
    if (highlightCommentIds.length === 0) return;
    if (!hasMoreComments || isLoadingMoreComments) return;
    if (highlightLocation) return;

    void handleLoadMoreComments();
  }, [
    highlightCommentIds,
    hasMoreComments,
    isLoadingMoreComments,
    highlightLocation,
    handleLoadMoreComments,
  ]);

  const exhaustedHighlightKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      highlightCommentIds.length === 0 ||
      highlightLocation ||
      hasMoreComments ||
      isLoadingMoreComments
    ) {
      if (highlightCommentIds.length === 0 || highlightLocation) {
        exhaustedHighlightKeyRef.current = null;
      }
      return;
    }

    const highlightKey = highlightCommentIds.join(",");
    if (exhaustedHighlightKeyRef.current === highlightKey) return;
    exhaustedHighlightKeyRef.current = highlightKey;
    onHighlightExhausted?.();
  }, [
    hasMoreComments,
    highlightCommentIds,
    highlightLocation,
    isLoadingMoreComments,
    onHighlightExhausted,
  ]);

  const lastScrolledIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (!highlightLocation || !shouldScrollToComment) return;

    const targetCommentId = highlightLocation.commentId;
    if (lastScrolledIdRef.current === targetCommentId) return;

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    const startedAt = Date.now();
    const locateRenderedComment = () => {
      if (cancelled) return;
      const target = document.querySelector<HTMLElement>(
        `[data-comment-id="${targetCommentId}"]`,
      );
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "center" });
        lastScrolledIdRef.current = targetCommentId;
        onHighlightLocated?.(targetCommentId);
        return;
      }
      if (Date.now() - startedAt < 5_000) {
        retryTimer = setTimeout(locateRenderedComment, 50);
      }
    };

    locateRenderedComment();
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [highlightLocation, onHighlightLocated, shouldScrollToComment]);
};
